import { MouseEvent, useEffect, useState } from 'react';
import { HostingType } from '@bumble/app/__generated__/data-contracts';
import { Loading } from '@bumble/app/components/Loading';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import ExpandLessOutlinedIcon from '@mui/icons-material/ExpandLessOutlined';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import SettingsSystemDaydreamOutlinedIcon from '@mui/icons-material/SettingsSystemDaydreamOutlined';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';

const toggleStyles = { textTransform: 'none', fontWeight: 700 };
interface AppServiceResizeProps {
  onSubmit: () => void;
  id: string;
}
export const AppServiceResize = ({ onSubmit, id }: AppServiceResizeProps) => {
  const api = useApi();
  const [hosting, setHosting] = useState<string | null>(null);
  const [size, setSize] = useState<string>('up');
  const [disableSubmit, setDisableSubmit] = useState<boolean>(false);

  const handleChange = (e: MouseEvent<HTMLElement>, newSize: string) => {
    setSize(newSize);
  };

  const { isLoading, data } = useQuery(
    ['appServiceHosting'],
    api.appServiceGetHostingApiV1AppServiceGetHostingGet.bind(null, {
      app_service_id: id,
      hosting_type: HostingType.Cloud,
    }),
    { enabled: true },
  );

  useEffect(() => {
    if (data?.data && data?.data.length > 0 && data?.data[0].hosting_id) {
      setHosting(data?.data[0].hosting_id);
    }
  }, [data]);

  const appServiceResize = useMutation(api.resizeAppServiceApiV1ResizeAppServicePost, {
    onSuccess() {
      setDisableSubmit(false);
      toast.success('Resized App Service successfully.');
      onSubmit();
    },
    onError: (error) => {
      apiError(error);
      setDisableSubmit(false);
    },
  });

  const handleSubmit = () => {
    if (!hosting) {
      toast.error('Please select a hosting location.');
      return;
    }
    setDisableSubmit(true);
    appServiceResize.mutate({
      app_service_id: id,
      hosting_id: hosting,
      direction: size,
    });
  };

  if (isLoading) {
    return <Loading />;
  }

  if (!isLoading && data?.data && data?.data.length < 0) {
    return <Alert severity="error">No hosting available.</Alert>;
  }

  return (
    <Box pt={2}>
      <Typography variant="body1" sx={{ mb: 6 }}>
        Enhance the performance of your cloud deployed App Service with dynamic resizing. This
        functionality allows for real-time adjustments, ensuring your app services always operate at
        peak efficiency.
      </Typography>
      <FormControl fullWidth sx={{ mb: 4 }}>
        <InputLabel id="hosting-label" shrink>
          Location
        </InputLabel>
        <Select
          labelId="hosting-label"
          id="hosting"
          label="Hosting"
          value={hosting}
          onChange={(e) => setHosting(e.target.value)}
          notched
        >
          {data?.data.map(({ hosting_id }) => (
            <MenuItem key={hosting_id} value={hosting_id}>
              {hosting_id}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Box display="flex" justifyContent="center" sx={{ mb: 4 }}>
        <ToggleButtonGroup
          aria-label="Resize App Service"
          value={size}
          onChange={handleChange}
          exclusive
          color="primary"
          size="small"
        >
          <ToggleButton value="up" sx={toggleStyles}>
            <ExpandLessOutlinedIcon />
            Size Up
          </ToggleButton>
          <ToggleButton value="" disabled>
            <SettingsSystemDaydreamOutlinedIcon />
          </ToggleButton>
          <ToggleButton value="down" sx={toggleStyles}>
            Size Down
            <ExpandMoreOutlinedIcon />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Box display="flex" justifyContent="flex-end" sx={{ mt: 5 }}>
        <Button variant="outlined" onClick={handleSubmit} disabled={disableSubmit}>
          {disableSubmit ? (
            <>
              <CircularProgress size={16} sx={{ mr: 1 }} color="inherit" />
              Working...
            </>
          ) : (
            'Confirm'
          )}
        </Button>
      </Box>
    </Box>
  );
};
